import React, { useState, useEffect, useRef } from "react";
import { Howl } from "howler";
import "./FooterPlayer.css";

// Same audio source and episode list as the original MUI version — only the
// UI layer changed (plain markup + FooterPlayer.css instead of MUI
// Box/Drawer/Grid), so this drops in without pulling MUI back into a
// redesign that's otherwise moved off it.
const MIHU_RADIO_LOGO = "/images/mihu-radio-flyer.png";
const MIHU_LOGO = "/images/mihu-logo-trans.png";
const WAVEFORM_IMAGE = "/images/indalah-waveform.png";

// Minimal inline icons — thin-line, rounded, matching the brand kit's
// iconography direction instead of pulling in @mui/icons-material.
function PlayIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 4.5v15l13-7.5-13-7.5z" fill="currentColor" stroke="none" />
    </svg>
  );
}
function PauseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="4.5" width="4" height="15" rx="1" fill="currentColor" stroke="none" />
      <rect x="14" y="4.5" width="4" height="15" rx="1" fill="currentColor" stroke="none" />
    </svg>
  );
}
function SkipNextIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 4.5v15l11-7.5-11-7.5z" fill="currentColor" stroke="none" />
      <rect x="18" y="4.5" width="2" height="15" fill="currentColor" stroke="none" />
    </svg>
  );
}
function PlaylistIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 6h12M4 12h12M4 18h8" />
      <path d="M18 10v8M15 15l3 3 3-3" />
    </svg>
  );
}
function ChevronDownIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}
function RadioIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="14" r="6" />
      <path d="M8 10l8-6M4 20h16" />
    </svg>
  );
}

export default function FooterPlayer({ source = "radio" }) {
  const [episodes, setEpisodes] = useState([]);
  const [currentTrack, setCurrentTrack] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [player, setPlayer] = useState(null);
  const [timecode, setTimecode] = useState("0:00");
  const [playlistOpen, setPlaylistOpen] = useState(false);

  const intervalRef = useRef(null);

  // source="radio" (default, used on /, /music, /radio, /featured) loads
  // real radio episodes. source="featured" (used on /events) loads the 4
  // real audio-type rows from release_locations instead — same table
  // MusicPage's map explorer already draws from.
  useEffect(() => {
    if (source === "featured") {
      fetch("/api/releases/map")
        .then((r) => r.json())
        .then((data) => {
          const list = Array.isArray(data) ? data : [];
          const featured = list
            .filter((t) => t.audio)
            .slice(0, 4)
            .map((t) => ({ title: `${t.artist} — ${t.song}`, audio_url: t.audio }));
          setEpisodes(featured);
        })
        .catch((err) => console.error("Error fetching featured songs:", err));
    } else {
      fetch("/api/radio-episodes")
        .then((r) => r.json())
        .then((data) => {
          if (data.success) {
            // Footer player only knows how to play direct audio files,
            // not embedded players — filter to rows that actually have one.
            setEpisodes((data.episodes || []).filter((e) => e.audio_url));
          }
        })
        .catch((err) => console.error("Error fetching radio episodes:", err));
    }
  }, [source]);

  useEffect(() => {
    if (!episodes.length) return;
    if (player) player.unload();

    const newPlayer = new Howl({
      src: [episodes[currentTrack].audio_url],
      html5: true,
      onend: () => nextTrack(),
    });

    setPlayer(newPlayer);
    if (isPlaying) newPlayer.play();

    return () => {
      newPlayer.unload();
      clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrack]);

  function togglePlay() {
    if (!player) return;
    if (isPlaying) {
      player.pause();
      setIsPlaying(false);
      clearInterval(intervalRef.current);
    } else {
      player.play();
      setIsPlaying(true);
      startTimer();
    }
  }

  function nextTrack() {
    if (player) player.stop();
    setCurrentTrack((prev) => (prev + 1) % episodes.length);
    setIsPlaying(true);
  }

  function selectTrack(index) {
    if (player) player.stop();
    setCurrentTrack(index);
    setIsPlaying(true);
    setPlaylistOpen(false);
  }

  function startTimer() {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (player) setTimecode(formatTime(player.seek() || 0));
    }, 500);
  }

  function formatTime(secs) {
    const minutes = Math.floor(secs / 60) || 0;
    const seconds = Math.floor(secs % 60) || 0;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  }

  return (
    <>
      {/* Playlist bottom sheet */}
      <div
        className={"fp-overlay" + (playlistOpen ? " open" : "")}
        onClick={() => setPlaylistOpen(false)}
        aria-hidden={!playlistOpen}
      />
      <div className={"fp-sheet" + (playlistOpen ? " open" : "")} role="dialog" aria-label="MIHU Radio playlist">
        <button className="fp-sheet-close" onClick={() => setPlaylistOpen(false)} aria-label="Close playlist">
          <ChevronDownIcon />
        </button>

        <div className="fp-sheet-body">
          <div className="fp-sheet-info">
            <img className="fp-sheet-flyer" src={MIHU_RADIO_LOGO} alt="MIHU Radio" />
            <div>
              <div className="fp-sheet-title">Music Is Healing Radio</div>
              <p className="fp-sheet-copy">
                Tune in to Music Is Healing Radio, where we explore the sounds, voices,
                and places of the MIHU collective — cultivating the four pillars of
                wellness (Mind, Body, Community, and Spirit). Live every Wednesday at
                8pm on WBCA 102.9FM, Boston's community radio station. Hosted by kwe.
              </p>
              <div className="fp-sheet-callout">
                To air an opinion, or share a PSA or upcoming event/project, email{" "}
                <strong>musicishealingus@gmail.com</strong> or call <strong>617 397 4440</strong>.
              </div>
            </div>
          </div>

          <div className="fp-sheet-section-label">Podcast episodes</div>
          <ul className="fp-episode-list">
            {episodes.map((track, index) => (
              <li key={track.title}>
                <button
                  className={"fp-episode" + (currentTrack === index ? " active" : "")}
                  onClick={() => selectTrack(index)}
                >
                  <RadioIcon />
                  <span>{track.title} - {track.description}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Fixed player bar */}
      <div className="fp-bar">
        <div className="fp-bar-inner">
          <img className="fp-bar-logo" src={MIHU_LOGO} alt="MIHU" />

          <button className="fp-bar-btn" onClick={togglePlay} aria-label={isPlaying ? "Pause" : "Play"}>
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>
          <button className="fp-bar-btn" onClick={nextTrack} aria-label="Next episode">
            <SkipNextIcon />
          </button>
          <button className="fp-bar-btn fp-bar-btn-accent" onClick={() => setPlaylistOpen(true)} aria-label="Open playlist">
            <PlaylistIcon />
          </button>

          <div className="fp-bar-nowplaying">
            <div className="fp-bar-title">{episodes[currentTrack]?.title}</div>
            <div className="fp-bar-time">{timecode}</div>
          </div>

          <div className="fp-bar-waveform">
            <img
              src={WAVEFORM_IMAGE}
              alt=""
              style={{ opacity: isPlaying ? 1 : 0.4, transition: "opacity 0.3s" }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
